// Offers & Applications store — fully backed by Supabase (no local/in-memory data).
// The public API mirrors the previous local store to avoid regressions; mutations
// are optimistic and persisted through `@/services/offers`.

import { useState, useCallback, useEffect, useRef } from 'react';
import * as offersApi from '@/services/offers';
import { uploadDataUrl } from '@/services/storage';
import { track, AnalyticsEvent } from '@/lib/analytics';
import type {
  Offer,
  Application,
  ApplicationStatus,
  OfferPricing,
  OfferRegistration,
  OfferStatus,
  OfferType,
  PricingMode,
} from '@/types/offer';

export type {
  Offer,
  Application,
  ApplicationStatus,
  OfferPricing,
  OfferRegistration,
  OfferStatus,
  OfferType,
  PricingMode,
};

/** Kept for backwards compatibility with older imports. */
export interface OfferNotification {
  id: string;
  userId: string;
  type: 'new_application' | 'application_accepted' | 'application_rejected';
  actorId: string;
  targetId: string;
  createdAt: string;
  read: boolean;
}

export function useOfferStore(currentUserId: string) {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [savedOfferIds, setSavedOfferIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const pageRef = useRef(0);
  const signedIn = !!currentUserId && currentUserId !== '__none__';

  const load = useCallback(async () => {
    if (!signedIn) {
      setOffers([]);
      setApplications([]);
      setSavedOfferIds([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [{ offers: firstPage, hasMore: more }, apps, saved] = await Promise.all([
        offersApi.fetchOffersPage(0),
        offersApi.fetchVisibleApplications(),
        offersApi.fetchSavedOfferIds(currentUserId),
      ]);
      pageRef.current = 0;
      setOffers(firstPage);
      setHasMore(more);
      setApplications(apps);
      setSavedOfferIds(saved);
    } catch (err) {
      console.error('[offers] load failed', err);
      setError(err instanceof Error ? err.message : 'Failed to load offers');
    } finally {
      setLoading(false);
    }
  }, [currentUserId, signedIn]);

  useEffect(() => { void load(); }, [load]);

  const loadMore = useCallback(async () => {
    if (!signedIn || !hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = pageRef.current + 1;
      const { offers: page, hasMore: more } = await offersApi.fetchOffersPage(next);
      pageRef.current = next;
      setOffers((prev) => {
        const known = new Set(prev.map((o) => o.id));
        return [...prev, ...page.filter((o) => !known.has(o.id))];
      });
      setHasMore(more);
    } catch (err) {
      console.error('[offers] loadMore failed', err);
    } finally {
      setLoadingMore(false);
    }
  }, [signedIn, hasMore, loadingMore]);

  // ------------------------------------------------------------------ offers

  const createOffer = useCallback(
    async (data: Omit<Offer, 'id' | 'createdById' | 'createdAt'>): Promise<string | undefined> => {
      if (!signedIn) return undefined;
      try {
        const coverImage = await uploadDataUrl('offer-media', currentUserId, data.coverImage);
        const created = await offersApi.insertOffer(currentUserId, { ...data, coverImage });
        setOffers((prev) => [created, ...prev]);
        void track(AnalyticsEvent.OFFER_CREATED, { offer_id: created.id, offer_type: data.type });
        return created.id;
      } catch (err) {
        console.error('[offers] createOffer failed', err);
        return undefined;
      }
    },
    [currentUserId, signedIn],
  );

  const updateOffer = useCallback(
    async (offerId: string, patch: Partial<Omit<Offer, 'id' | 'createdById' | 'createdAt'>>) => {
      setOffers((prev) => prev.map((o) => (o.id === offerId ? { ...o, ...patch } : o)));
      try {
        const next = patch.coverImage
          ? { ...patch, coverImage: await uploadDataUrl('offer-media', currentUserId, patch.coverImage) }
          : patch;
        await offersApi.patchOffer(offerId, next);
      } catch (err) {
        console.error('[offers] updateOffer failed', err);
        void load();
      }
    },
    [load, currentUserId],
  );

  const deleteOffer = useCallback(
    async (offerId: string) => {
      setOffers((prev) => prev.filter((o) => o.id !== offerId));
      setApplications((prev) => prev.filter((a) => a.offerId !== offerId));
      try {
        await offersApi.removeOffer(offerId);
      } catch (err) {
        console.error('[offers] deleteOffer failed', err);
        void load();
      }
    },
    [load],
  );

  const setStatus = useCallback(
    (offerId: string, status: OfferStatus) => updateOffer(offerId, { status }),
    [updateOffer],
  );

  const archiveOffer = useCallback((offerId: string) => setStatus(offerId, 'archived'), [setStatus]);
  const restoreOffer = useCallback((offerId: string) => setStatus(offerId, 'active'), [setStatus]);

  /** Fetch a single offer (used by detail views for offers outside the loaded pages). */
  const fetchOffer = useCallback(async (offerId: string): Promise<Offer | null> => {
    if (!signedIn) return null;
    try {
      const found = await offersApi.fetchOfferById(offerId);
      if (found) {
        setOffers((prev) =>
          prev.some((o) => o.id === found.id)
            ? prev.map((o) => (o.id === found.id ? found : o))
            : [found, ...prev],
        );
      }
      return found;
    } catch (err) {
      console.error('[offers] fetchOffer failed', err);
      return null;
    }
  }, [signedIn]);

  // ----------------------------------------------------------- registrations

  const registerForOffer = useCallback(
    async (offerId: string) => {
      if (!signedIn) return;
      setOffers((prev) =>
        prev.map((o) => {
          if (o.id !== offerId) return o;
          const regs = o.registrations ?? [];
          if (regs.some((r) => r.userId === currentUserId)) return o;
          return { ...o, registrations: [...regs, { userId: currentUserId, at: new Date().toISOString() }] };
        }),
      );
      try {
        await offersApi.insertRegistration(offerId, currentUserId);
      } catch (err) {
        console.error('[offers] registerForOffer failed', err);
        void load();
      }
    },
    [currentUserId, signedIn, load],
  );

  const unregisterFromOffer = useCallback(
    async (offerId: string) => {
      if (!signedIn) return;
      setOffers((prev) =>
        prev.map((o) =>
          o.id === offerId
            ? { ...o, registrations: (o.registrations ?? []).filter((r) => r.userId !== currentUserId) }
            : o,
        ),
      );
      try {
        await offersApi.removeRegistration(offerId, currentUserId);
      } catch (err) {
        console.error('[offers] unregisterFromOffer failed', err);
        void load();
      }
    },
    [currentUserId, signedIn, load],
  );

  const isRegistered = useCallback(
    (offerId: string) =>
      !!offers.find((o) => o.id === offerId)?.registrations?.some((r) => r.userId === currentUserId),
    [offers, currentUserId],
  );

  // ------------------------------------------------------------- applications

  const applyToOffer = useCallback(
    async (offerId: string, extra?: { message?: string; documentUrl?: string }) => {
      if (!signedIn) return;
      if (applications.some((a) => a.offerId === offerId && a.applicantId === currentUserId)) return;
      try {
        const created = await offersApi.insertApplication(offerId, currentUserId, extra);
        setApplications((prev) =>
          prev.some((a) => a.id === created.id) ? prev : [created, ...prev],
        );
        void track(AnalyticsEvent.APPLICATION_SUBMITTED, { offer_id: offerId });
      } catch (err) {
        console.error('[offers] applyToOffer failed', err);
      }
    },
    [applications, currentUserId, signedIn],
  );

  const hasApplied = useCallback(
    (offerId: string) =>
      applications.some((a) => a.offerId === offerId && a.applicantId === currentUserId),
    [applications, currentUserId],
  );

  const getApplicationsForOffer = useCallback(
    (offerId: string) => applications.filter((a) => a.offerId === offerId),
    [applications],
  );

  const getApplicationById = useCallback(
    (applicationId: string) => applications.find((a) => a.id === applicationId),
    [applications],
  );

  const getApplicationsByCreator = useCallback(
    (creatorId: string) => {
      const myOfferIds = new Set(offers.filter((o) => o.createdById === creatorId).map((o) => o.id));
      return applications.filter((a) => myOfferIds.has(a.offerId));
    },
    [applications, offers],
  );

  const getApplicationsByApplicant = useCallback(
    (userId: string) => applications.filter((a) => a.applicantId === userId),
    [applications],
  );

  const withdrawApplication = useCallback(
    async (applicationId: string) => {
      const target = applications.find((a) => a.id === applicationId);
      if (!target || target.applicantId !== currentUserId || target.status !== 'pending') return;
      setApplications((prev) => prev.filter((a) => a.id !== applicationId));
      try {
        await offersApi.removeApplication(applicationId);
      } catch (err) {
        console.error('[offers] withdrawApplication failed', err);
        void load();
      }
    },
    [applications, currentUserId, load],
  );

  const updateApplicationStatus = useCallback(
    async (applicationId: string, status: 'accepted' | 'rejected') => {
      setApplications((prev) => prev.map((a) => (a.id === applicationId ? { ...a, status } : a)));
      try {
        await offersApi.patchApplicationStatus(applicationId, status);
      } catch (err) {
        console.error('[offers] updateApplicationStatus failed', err);
        void load();
      }
    },
    [load],
  );

  // ------------------------------------------------------------ saved offers

  const isOfferSaved = useCallback(
    (offerId: string) => savedOfferIds.includes(offerId),
    [savedOfferIds],
  );

  const toggleSaveOffer = useCallback(
    async (offerId: string) => {
      if (!signedIn) return;
      const saved = savedOfferIds.includes(offerId);
      setSavedOfferIds((prev) => (saved ? prev.filter((id) => id !== offerId) : [...prev, offerId]));
      try {
        if (saved) await offersApi.unsaveOffer(offerId, currentUserId);
        else await offersApi.saveOffer(offerId, currentUserId);
      } catch (err) {
        console.error('[offers] toggleSaveOffer failed', err);
        void load();
      }
    },
    [savedOfferIds, currentUserId, signedIn, load],
  );

  /** Legacy no-op: offer notifications are handled by the notification store. */
  const getNotificationsForUser = useCallback((_userId: string): OfferNotification[] => [], []);

  return {
    offers,
    applications,
    savedOfferIds,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refresh: load,
    createOffer,
    updateOffer,
    deleteOffer,
    archiveOffer,
    restoreOffer,
    fetchOffer,
    registerForOffer,
    unregisterFromOffer,
    isRegistered,
    applyToOffer,
    hasApplied,
    getApplicationsForOffer,
    getApplicationById,
    getApplicationsByCreator,
    getApplicationsByApplicant,
    withdrawApplication,
    updateApplicationStatus,
    isOfferSaved,
    toggleSaveOffer,
    getNotificationsForUser,
  };
}
